import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaTrash, FaUser, FaCalendar, FaArrowRightArrowLeft } from 'react-icons/fa6';
import { toast } from 'react-toastify';
import ModalLayout from '../layouts/ModalLayout';
import Button from '../buttons/Button';
import AreYouSureModal from './AreYouSureModal';
import MovePhotoModal from './MovePhotoModal';
import ApiImage from '../misc/ApiImage';
import { isVideoFile } from '../../../utils/mediaUtils';

interface PhotoViewerModalProps {
  open: boolean;
  showMetadata: boolean;
  setOpen: (open: boolean) => void;
  photo: Photo | null;
  currentAlbumId: string;
  onDelete: (photoId: number) => void;
  onMove?: (photoId: number) => void;
}

export default function PhotoViewerModal({
  open,
  showMetadata,
  setOpen,
  photo,
  currentAlbumId,
  onDelete,
  onMove
}: PhotoViewerModalProps): JSX.Element {
  const { t } = useTranslation();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);

  if (!photo) {
    return <></>;
  }

  const isVideo = isVideoFile(photo.pictureUrl);

  const handleDownload = async () => {
    if (!photo?.pictureUrl) return;
    try {

      const response = await fetch(photo.pictureUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const extension = photo.pictureUrl.split('.').pop()?.toLowerCase() || (isVideo ? 'mp4' : 'jpg');
      link.download = `${isVideo ? 'video' : 'photo'}-${photo.id}-${new Date(photo.createdAt).toISOString().split('T')[0]}.${extension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('toast.error.unexpected'));
    }
  };

  const handlePhotoMoved = (photoId: number | number[]) => {
    if (onMove) {
      const singlePhotoId = Array.isArray(photoId) ? photoId[0] : photoId;
      onMove(singlePhotoId);
    }
    setOpen(false);
  };

  const handleDeleteConfirm = () => {
    onDelete(photo.id);
    setDeleteModalOpen(false);
    setOpen(false);
  };

  return (
    <>
      <ModalLayout
        open={open}
        setOpen={setOpen}
        className="relative transform overflow-hidden rounded-[20px] bg-secondary-50 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl"
        closeButton
        preventClose={moveModalOpen}
      >
        <div className="relative">
          <div className="relative">
            {isVideo ? (
              <video
                src={photo.pictureUrl}
                className="w-full max-h-[70vh] object-contain rounded-t-[20px]"
                controls
                autoPlay
                preload="metadata"
              >
                <track kind="captions" />
                Your browser does not support the video tag.
              </video>
            ) : (
              <ApiImage
                path={photo.pictureUrl}
                alt={`Photo-${photo.createdAt}`}
                className="w-full max-h-[70vh] object-contain rounded-t-[20px]"
              />
            )}
          </div>

          <div className="p-6">
            {showMetadata && (
              <div className="mb-4 flex flex-col gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FaUser className="h-4 w-4" />
                  <span>{`${t('page.photoAlbum.uploadedBy')}: ${photo.uploader.firstName} ${photo.uploader.lastName}`}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FaCalendar className="h-4 w-4" />
                  <span>{`${t('page.photoAlbum.uploadedOn')}: ${new Date(photo.createdAt).toLocaleDateString()}`}</span>
                </div>
              </div>
            )}

            <div className="flex justify-between gap-3">
              <Button
                variant="danger-v2"
                onClick={() => setDeleteModalOpen(true)}
                className="flex items-center gap-2"
              >
                <FaTrash className="h-4 w-4" />
                {t('general.delete')}
              </Button>

              <div className="flex gap-2">
                {onMove && (
                  <Button
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMoveModalOpen(true);
                    }}
                    className="flex items-center gap-2"
                  >
                    <FaArrowRightArrowLeft className="h-4 w-4" />
                    {t('page.photoAlbum.movePhoto.move')}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onClick={handleDownload}
                  className="flex items-center gap-2"
                >
                  <FaDownload className="h-4 w-4" />
                  {t('page.photoAlbum.download')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ModalLayout>

      <AreYouSureModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        title={t('page.photoAlbum.deletePhoto.confirmTitle')}
        description={t('page.photoAlbum.deletePhoto.confirmDescription')}
        onConfirm={handleDeleteConfirm}
        saveClassName="bg-danger text-secondary-50"
      />

      <MovePhotoModal
        open={moveModalOpen}
        setOpen={setMoveModalOpen}
        photo={photo}
        currentAlbumId={currentAlbumId}
        onPhotoMoved={handlePhotoMoved}
      />
    </>
  );
}
